import { styleConstants } from 'widgets/portfolioPl/variables.module.constants';
import { useWidgetPortfolioSelectionStatus } from 'widgets/hooks';
import { usePortfolioPlConfigSlice } from 'widgets/portfolioPl/store/config/configSlice';
import { useWidgetContext } from 'widgets/WidgetContext';
import { usePortfolioPlLegendStatus } from 'widgets/portfolioPl/chart/hooks';
import { useSelectLocked } from 'layoutRenderer/store/selectors';

export interface PortfolioPlDimensions {
    chartsHeight: number;
    performanceChart: {
        legendsMarginTop: number; // Legends use margins for the horizontal scroll to work ie their position cannot be absolute
        chartTop: number;
        chartHeight: number;
    };
    leavesChart: {
        legendsMarginTop: number;
        chartTop: number;
        chartHeight: number;
    };
}

export function usePortfolioPlDimensions(): PortfolioPlDimensions {
    const { height } = useWidgetContext();
    const { config } = usePortfolioPlConfigSlice();
    const { hasValidSelection } = useWidgetPortfolioSelectionStatus();
    const { hideToolbar, leavesHidden, leavesCollapsed } = config;
    const { hidePerformanceLegends, hideLeavesLegends } =
        usePortfolioPlLegendStatus();
    const locked = useSelectLocked();

    const toolbarIsVisible = hasValidSelection && !hideToolbar;
    const toolbarConsumedHeight = toolbarIsVisible
        ? styleConstants.panelHeaderHeight
        : 0;

    // Work out the heights of the two charts...
    const totalChartsHeight =
        height -
        toolbarConsumedHeight -
        // This gives room for the horizontal scrollbar when needed...
        styleConstants.spaceRound * 2;

    // Leaves may be hidden, or could be collapsed i.e. have only a collapsed toolbar
    const leavesChartContainerHeight = leavesHidden
        ? 0
        : leavesCollapsed && locked && hideLeavesLegends
        ? 0
        : leavesCollapsed && !hideLeavesLegends
        ? styleConstants.companionChartCollapsedHeight +
          styleConstants.spaceRound * 2
        : totalChartsHeight * 0.35;

    const performanceChartContainerHeight =
        totalChartsHeight - leavesChartContainerHeight;

    // Work out the positions of the performance chart...
    const performanceChartLegendsMarginTop = toolbarConsumedHeight;

    const performanceChartLegendConsumedHeight = hidePerformanceLegends
        ? 0
        : styleConstants.legendHeight;

    const performanceChartChartTop =
        performanceChartLegendsMarginTop +
        performanceChartLegendConsumedHeight +
        styleConstants.spaceRound;

    const performanceChartHeight =
        performanceChartContainerHeight - performanceChartLegendConsumedHeight;

    // Work out the positions of the leaves chart
    const leavesChartLegendsMarginTop = hidePerformanceLegends
        ? toolbarConsumedHeight + performanceChartContainerHeight
        : performanceChartHeight;

    const leavesChartLegendConsumedHeight = hideLeavesLegends
        ? 0
        : styleConstants.legendHeight;

    const leavesChartTop =
        toolbarConsumedHeight +
        performanceChartContainerHeight +
        leavesChartLegendConsumedHeight +
        styleConstants.spaceRound;
    const leavesChartHeight =
        leavesChartContainerHeight - leavesChartLegendConsumedHeight;

    return {
        chartsHeight: totalChartsHeight,
        performanceChart: {
            legendsMarginTop: performanceChartLegendsMarginTop,
            chartTop: performanceChartChartTop,
            chartHeight: performanceChartHeight,
        },
        leavesChart: {
            legendsMarginTop: leavesChartLegendsMarginTop,
            chartTop: leavesChartTop,
            chartHeight: leavesChartHeight - styleConstants.spaceRound,
        },
    };
}
